import { useTranslation } from "react-i18next";
import { useStatusModal } from "shared/core/hooks/useStatusModal";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { useModalContext } from "shared/core/state/ModalContext/ModalContext";
import {
  useAcceptUserTermsFunctionRequest,
  useGetGenesysWhatsAppAvailabilityFunctionRequest,
  useGetUserTermsStatusFunctionRequest,
  useLogReportingEventFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { WhatsAppTermsModal } from "../../Components/WhatsAppTermsModal/WhatsAppTermsModal";
import Modals from "core/enums/modals.enum";
import { useUserContext } from "core/state/UserContext/UserContext";
import useLocale from "core/hooks/useLocale/useLocale";
import { TermKeys } from "core/enums/enums";
import { trackEvents } from "core/monitoring/Events";
import { ReportingEventType } from "shared/constants/ReportingEventType";
import { EventNames } from "core/monitoring/types/enums";
import { ReactComponent as TimerSvg } from "shared/assets/Icons/refresh/Timer.svg";
import { externalLink } from "core/links";
import { generatePrefilledText } from "../../utils/generatePrefilledText/generatePrefilledText";
import { useCountdown } from "../../../../hooks/useCountdown/useCountdown";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";

export interface UseWhatsAppResult {
  startWhatsAppChat: () => void;
  ComingSoonModal: JSX.Element | null;
  TermsModal: JSX.Element | null;
  isCardDisabled: boolean;
  useWhatsAppIntegration: boolean;
  description: string;
}

export const useWhatsApp = (): UseWhatsAppResult => {
  const xClientCode = ClientInfoState.getCode();
  const { trigger } = useLogReportingEventFunctionRequest();
  const { t } = useTranslation(undefined, { keyPrefix: "support.whatsApp" });
  const { modalNotify } = useNotifyModalContext();
  const { setBody, Modal: ComingSoonModal } = useStatusModal(Modals.whatsAppComingSoon);
  const { isVisible: isTermsModalVisible, show: showTermsModal, hide: hideTermsModal } = useModalContext(Modals.WhatsAppTerms);
  const { trigger: triggerAcceptTerms, isMutating } = useAcceptUserTermsFunctionRequest({ xClientCode });
  const {
    data: userTermsStatus,
    isLoading: isLoadingTermsStatus,
    error: termsStatusError,
    mutate,
  } = useGetUserTermsStatusFunctionRequest({ xClientCode, key: TermKeys.Whatsapp });
  const { user } = useUserContext();
  const { isLocalePermitted } = useLocale();
  const {
    data: genesysAvailableStatus,
    isLoading: isLoadingGenesysStatus,
    error: genesysStatusError,
  } = useGetGenesysWhatsAppAvailabilityFunctionRequest({ xClientCode: ClientInfoState.getCode() }, { refreshInterval: 30000 });
  const { isRetrying, secondsLeft } = useCountdown(!!genesysAvailableStatus?.available, 30);

  const useWhatsAppIntegration = process.env.REACT_APP_USE_WHATSAPP_INTEGRATION === "true" && isLocalePermitted("whatsApp");

  const isWhatsAppUnavailable = process.env.REACT_APP_WHATSAPP_UNAVAILABLE === "true" || !!termsStatusError || !!genesysStatusError;

  const areCounsellorsAvailable = !!genesysAvailableStatus?.available;

  const isLoadingStatus = isLoadingGenesysStatus || isLoadingTermsStatus || isRetrying;

  const getDescription = () => {
    if (useWhatsAppIntegration) {
      if (isWhatsAppUnavailable) return t("unavailable");

      if (isLoadingStatus) return t("loading");

      if (!areCounsellorsAvailable) return t("counsellorUnavailable", { secondsLeft });
    }

    return t("description");
  };

  const isCardDisabled = useWhatsAppIntegration && (isWhatsAppUnavailable || !areCounsellorsAvailable || isLoadingStatus);

  const handleWhatsAppRedirect = () => {
    externalLink(`https://wa.me/${t("number")}?text=${generatePrefilledText(user)}`);
    trackEvents(EventNames.WHATSAPP_REDIRECT);
    trigger({ body: { reportingEventType: ReportingEventType.SupportWhatsApp } });
  };

  const handleClickContinue = async () => {
    try {
      handleWhatsAppRedirect();
      await triggerAcceptTerms({
        query: {
          key: TermKeys.Whatsapp,
        },
      });
      await mutate();
      hideTermsModal();
    } catch (err) {
      hideTermsModal();
      modalNotify("error", t("notice.errorTitle"), t("notice.errorMessage"), t("notice.errorCta"));
    }
  };

  const handleComingSoon = () =>
    setBody({
      data: {
        title: t("coming-soon.title"),
        description: t("coming-soon.description"),
        image: <TimerSvg width={50} height={50} />,
      },
      status: "info",
      dismissAfter: 5000,
      dismissCallback: () => setBody(null),
    });

  const startWhatsAppChat = () => {
    trackEvents(EventNames.WHATSAPP_CLICK);
    if (useWhatsAppIntegration) {
      if (!!userTermsStatus?.accepted) {
        handleWhatsAppRedirect();
      } else {
        showTermsModal();
      }
    } else {
      handleComingSoon();
    }
  };

  const TermsModal = isTermsModalVisible ? (
    <WhatsAppTermsModal handleClickContinue={handleClickContinue} handleClickCancel={hideTermsModal} isSending={isMutating} />
  ) : null;

  return {
    startWhatsAppChat,
    ComingSoonModal,
    TermsModal,
    isCardDisabled,
    useWhatsAppIntegration,
    description: getDescription(),
  };
};
